import type { FC, ReactNode } from 'react'

import { Fragment } from 'react'
import { cn } from '@/lib/utils'
import { useCountdown } from '@/hooks/use-countdown'
import PunctuatedText from '@/components/landing/punctuated-text'

import { campaign } from '@/constants/campaign'

interface CountdownProps {
    className?: string
    // Always the 2x2 grid, even on wide or landscape screens (for narrow side placements).
    stacked?: boolean
}

interface GroupProps {
    value: number
    label: string
    ready: boolean
}

interface DigitProps {
    value: string
    ready: boolean
}

type UnitKey = keyof typeof campaign.units

// An absolute instant: the offset in the setting makes it identical for every visitor.
const DEADLINE = Date.parse(campaign.deadline)

const UNITS: ReadonlyArray<{ key: UnitKey, unit: string }> = [
    { key: 'days', unit: 'day' },
    { key: 'hours', unit: 'hour' },
    { key: 'minutes', unit: 'minute' },
    { key: 'seconds', unit: 'second' }
]

const UNIT_FORMATS = UNITS.map(({ unit }) => new Intl.NumberFormat('en', { style: 'unit', unit, unitDisplay: 'long' }))
const LIST_FORMAT = new Intl.ListFormat('en', { style: 'long', type: 'conjunction' })

// Whole 24 hour days, then the hours, minutes and seconds left over.
const splitDuration = (totalSeconds: number): Record<UnitKey, number> => ({
    days: Math.floor(totalSeconds / 86400),
    hours: Math.floor(totalSeconds / 3600) % 24,
    minutes: Math.floor(totalSeconds / 60) % 60,
    seconds: totalSeconds % 60
})

// "15 days, 7 hours, 42 minutes, and 9 seconds" for screen readers.
const describe = (parts: Record<UnitKey, number>): string => {
    return LIST_FORMAT.format(UNITS.map(({ key }, index) => UNIT_FORMATS[index].format(parts[key])))
}

// Each digit sits in a fixed slot (one zero wide plus room for the outlines), so changing numbers never shift the layout.
const Digit: FC<DigitProps> = ({ value, ready }): ReactNode => {
    return (
        <span className='inline-flex w-[calc(1ch_+_0.14em)] justify-center'>
            <span
                key={ready ? value : 'placeholder'}
                data-digit={value}
                className={cn('jersey-digit inline-block', ready ? 'motion-safe:animate-digit-in' : 'invisible')}
            >
                {value}
            </span>
        </span>
    )
}

const Group: FC<GroupProps> = ({ value, label, ready }): ReactNode => {
    const digits = String(value).padStart(2, '0').split('')

    return (
        <div className='flex flex-col items-center'>
            <div className='mb-[clamp(1rem,0.2em,2.25rem)] flex font-numbers font-bold leading-[0.85]'>
                {digits.map((digit, index) => (
                    <Digit key={digits.length - index} value={digit} ready={ready} />
                ))}
            </div>

            <span className='-mr-[0.32em] text-[length:clamp(0.6875rem,0.55rem_+_0.35vw,0.875rem)] font-semibold leading-none tracking-[0.32em] text-foreground/70'>
                {label}
            </span>
        </div>
    )
}

// Two small red squares, centered on the height of the digits. Only shown in the single row layout.
const Separator: FC = (): ReactNode => {
    return (
        <span className='hidden h-[0.85em] flex-col items-center justify-center gap-[0.2em] pt-[0.06em] pb-[0.04em] timer-row:flex'>
            <span className='size-[0.065em] bg-primary' />
            <span className='size-[0.065em] bg-primary' />
        </span>
    )
}

const Countdown: FC<CountdownProps> = ({ className, stacked = false }): ReactNode => {
    const secondsLeft = useCountdown(DEADLINE)
    const ready = secondsLeft !== null
    const isOver = secondsLeft === 0
    const parts = splitDuration(secondsLeft ?? 0)

    return (
        <div id='countdown' className={cn('@container relative w-full max-w-[96rem]', className)}>
            <div
                aria-hidden='true'
                className='pointer-events-none absolute top-1/2 left-1/2 -z-10 h-[min(90svh,50rem)] w-[min(135vw,112rem)] -translate-x-1/2 -translate-y-1/2'
            >
                <div className='campaign-glow size-full opacity-90 motion-safe:animate-glow-breathe' />
            </div>

            {isOver ? (
                <p
                    aria-hidden='true'
                    className={cn(
                        'text-balance text-center font-heading font-extrabold leading-[0.9] tracking-[0.01em] text-white',
                        'text-[length:min(22cqi,14svh)]',
                        !stacked && 'timer-row:text-[length:min(11cqi,24svh,9.5vw)]',
                        'motion-safe:animate-in motion-safe:fade-in motion-safe:duration-1000'
                    )}
                >
                    <PunctuatedText text={campaign.zeroStateMessage} />
                </p>
            ) : (
                <div role='timer' aria-live='off' aria-atomic='true'>
                    {ready && (
                        <span className='sr-only'>
                            {`${campaign.accessibility.timerPrefix} ${describe(parts)}`}
                        </span>
                    )}

                    <div
                        aria-hidden='true'
                        className={cn(
                            'grid grid-cols-[repeat(2,auto)] justify-center justify-items-center gap-x-[0.34em] gap-y-[0.24em]',
                            'font-heading font-extrabold tabular-nums text-white',
                            'text-[length:min(27cqi,17svh,9rem)]',
                            !stacked && 'timer-row:flex timer-row:items-start timer-row:gap-x-[0.18em] timer-row:text-[length:min(12.5cqi,28svh,10.5vw)]'
                        )}
                    >
                        {UNITS.map(({ key }, index) => (
                            <Fragment key={key}>
                                {index > 0 && !stacked && <Separator />}
                                <Group value={parts[key]} label={campaign.units[key]} ready={ready} />
                            </Fragment>
                        ))}
                    </div>
                </div>
            )}

            <p aria-live='polite' className='sr-only'>
                {isOver ? campaign.zeroStateMessage : ''}
            </p>
        </div>
    )
}

export default Countdown
